import { loadCss, loadScript } from '@/utils/common';
import { WUJI_DEPS_KEY } from './consts';
import { CDN_MODULES, getCdnModulePath } from './cdn-modules';
import { platformBuiltinModules } from './platform-builtin-modules';
export { CDN_MODULES } from './cdn-modules';
/**
 * 运行时模块系统
 *
 * 负责模块的注册、执行和解析，编辑时和运行时都需要它。
 * 与打包编排（DependencyLoader）完全解耦。
 */
export class ModuleRegistry {
    modules = {};
    loadedScripts = new Set();
    cdnModules = new Map();
    loadingCdnModules = new Map();
    constructor() {
        if (!window[WUJI_DEPS_KEY]) {
            window[WUJI_DEPS_KEY] = {};
        }
        this.seedPlatformBuiltinModules();
        window.npmLoader = this;
        this.registerExternalResolver();
    }
    /**
     * bundle 脚本执行后回调此方法注册模块
     *
     * 远程打包产物中会生成类似 `window.npmLoader.register(function() { return {...} })` 的调用
     */
    register(vendorFunction) {
        const newVendor = vendorFunction();
        Object.keys(newVendor).forEach((k) => {
            newVendor[k] = {
                ...newVendor[k],
                __esModule: true,
            };
        });
        Object.assign(this.modules, newVendor);
        Object.assign(window[WUJI_DEPS_KEY], newVendor);
    }
    /**
     * 统一加载入口：执行 bundle + 加载 CDN 依赖
     *
     * 调用方只需传入 bundle 和 dependencies，内部自动路由到 npm bundle 执行或 CDN 加载。
     */
    async load({ bundle, dependencies }) {
        const cdnDeps = (dependencies || []).filter(dep => CDN_MODULES.includes(dep));
        await Promise.all([
            bundle?.js ? this.executeBundle(bundle) : Promise.resolve(),
            cdnDeps.length > 0 ? this.loadCdn(cdnDeps) : Promise.resolve(),
        ]);
    }
    async executeBundle(bundle) {
        if (bundle.js && !this.loadedScripts.has(bundle.js)) {
            await loadScript(bundle.js);
            this.loadedScripts.add(bundle.js);
        }
        if (bundle.css && !this.loadedScripts.has(bundle.css)) {
            await loadCss(bundle.css);
            this.loadedScripts.add(bundle.css);
        }
    }
    async loadCdn(dependencies) {
        if (!dependencies.length)
            return;
        await Promise.all(dependencies.map(moduleName => this.loadCdnModule(moduleName)));
    }
    /**
     * 外部模块解析
     *
     * 远程打包产物中，所有 external 依赖的 import 会被转换为调用此函数，例如:
     *   import { createApp } from 'vue'  →  window._wuji_deps_loader('vue')
     *
     * 解析优先级: bundle 模块 > _wuji_deps > 平台内置模块 > Vue3 生态 > React 生态
     */
    resolve(moduleName) {
        if (this.modules[moduleName])
            return this.modules[moduleName];
        const deps = window[WUJI_DEPS_KEY];
        if (deps?.[moduleName])
            return deps[moduleName];
        if (platformBuiltinModules[moduleName]) {
            return platformBuiltinModules[moduleName]();
        }
        const vue3 = window.vue3 || window.Vue3;
        if (vue3) {
            if (moduleName === 'vue')
                return vue3.Vue3 || vue3;
            if (moduleName === 'vue-router')
                return vue3.vueRouter;
            if (moduleName === 'ant-design-vue')
                return vue3.antdModule;
        }
        if (moduleName === 'react')
            return window.React;
        if (moduleName === 'react-dom')
            return window.ReactDOM;
        if (moduleName === 'react-dom/client')
            return window.ReactDOMClient;
        if (moduleName === 'react/jsx-runtime')
            return window.ReactJSXRuntime;
        if (moduleName === 'react/jsx-dev-runtime')
            return window.ReactJSXDevRuntime;
        if (moduleName === 'react-router-dom')
            return window.ReactRouterDOM;
        console.warn(`[ModuleRegistry] Cannot resolve external module: ${moduleName}`);
        return undefined;
    }
    getModules() {
        return { ...this.modules };
    }
    debug(moduleName) {
        console.log('[ModuleRegistry] modules:', Object.keys(this.modules));
        console.log('[ModuleRegistry] loadedScripts:', Array.from(this.loadedScripts));
        console.log('[ModuleRegistry] cdnModules:', Array.from(this.cdnModules.keys()));
        if (moduleName && this.modules[moduleName]) {
            console.log(`[ModuleRegistry] ${moduleName}:`, this.modules[moduleName]);
        }
    }
    registerExternalResolver() {
        window._wuji_deps_loader = (moduleName) => this.resolve(moduleName);
    }
    loadCdnModule(moduleName) {
        if (this.cdnModules.has(moduleName)) {
            return Promise.resolve(this.cdnModules.get(moduleName));
        }
        const loadingModule = this.loadingCdnModules.get(moduleName);
        if (loadingModule)
            return loadingModule;
        window.require.config({
            paths: {
                [moduleName]: getCdnModulePath(moduleName),
            },
        });
        const promise = new Promise((resolve, reject) => {
            window.require([moduleName], resolve, reject);
        }).then((module) => {
            this.cdnModules.set(moduleName, module);
            window[WUJI_DEPS_KEY][moduleName] = module;
            return module;
        })
            .finally(() => {
            this.loadingCdnModules.delete(moduleName);
        });
        this.loadingCdnModules.set(moduleName, promise);
        return promise;
    }
    seedPlatformBuiltinModules() {
        const deps = window[WUJI_DEPS_KEY];
        Object.entries(platformBuiltinModules).forEach(([name, loader]) => {
            if (!deps[name]) {
                deps[name] = loader();
            }
        });
    }
}
const moduleRegistry = new ModuleRegistry();
export default moduleRegistry;
